import React, { useState } from 'react';
import {
  Box,
  Typography,
  Tooltip,
  IconButton,
  Paper,
  Popper,
  ClickAwayListener,
  useTheme
} from '@mui/material';
import {
  WbSunny,
  NightlightRound,
  Cloud,
  CloudQueue,
  WaterDrop,
  Grain,
  FlashOn,
  AcUnit,
  Visibility,
  Help,
  Air
} from '@mui/icons-material';

import weatherService from '../services/weatherService';

// Map icon names to actual Material UI icons
const iconMap = {
  WbSunny,
  NightlightRound,
  Cloud,
  CloudQueue,
  WaterDrop,
  Grain,
  FlashOn,
  AcUnit,
  Visibility,
  Help,
  Air
};

const WeatherDisplay = ({ weather, showDetails = false, size = 'small' }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  if (!weather || !weather.success) {
    return null; // Don't show anything for failed weather lookups
  }

  const IconComponent = iconMap[weatherService.getIconForCondition(weather.icon_code, weather.is_night)];
  const temperature = weatherService.formatTemperature(weather.temperature);

  const handleClick = (event) => {
    if (showDetails) {
      setAnchorEl(event.currentTarget);
      setDetailsOpen(true);
    }
  };

  const handleClose = () => {
    setDetailsOpen(false);
    setAnchorEl(null);
  };

  const weatherDetails = (
    <Paper 
      elevation={3} 
      sx={{ 
        p: 2, 
        maxWidth: 250,
        bgcolor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`
      }}
    >
      <Typography variant="subtitle2" gutterBottom>
        {weather.description}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography variant="body2">
          <strong>Temperature:</strong> {temperature}
        </Typography>
        <Typography variant="body2">
          <strong>Humidity:</strong> {weather.humidity}%
        </Typography>
        <Typography variant="body2">
          <strong>Pressure:</strong> {weather.pressure?.toFixed(2)} inHg
        </Typography>
        {weather.wind_speed > 0 && (
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Air fontSize="small" />
            <strong>Wind:</strong> {weather.wind_speed?.toFixed(1)} mph {weatherService.getWindDirection(weather.wind_direction)}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
          Updated: {new Date(weather.last_updated).toLocaleTimeString()}
        </Typography>
      </Box>
    </Paper>
  );

  const weatherIcon = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        cursor: showDetails ? 'pointer' : 'default',
        '&:hover': showDetails ? {
          opacity: 0.8
        } : {}
      }}
      onClick={handleClick}
    >
      <IconComponent 
        sx={{ 
          fontSize: size === 'small' ? 16 : 20,
          color: weather.is_night ? theme.palette.info.main : theme.palette.warning.main
        }} 
      />
      <Typography 
        variant={size === 'small' ? 'body2' : 'body1'}
        sx={{ 
          fontWeight: 500,
          color: theme.palette.text.primary
        }}
      >
        {temperature}
      </Typography>
    </Box>
  );

  if (showDetails) {
    return (
      <>
        {weatherIcon}
        <Popper
          open={detailsOpen}
          anchorEl={anchorEl}
          placement="bottom-end"
          sx={{ zIndex: theme.zIndex.tooltip }}
        >
          <ClickAwayListener onClickAway={handleClose}>
            {weatherDetails}
          </ClickAwayListener>
        </Popper>
      </>
    );
  }

  return (
    <Tooltip title={`${weather.description} • ${temperature}`} arrow>
      {weatherIcon}
    </Tooltip>
  );
};

export default WeatherDisplay;